import { useAtom } from 'jotai';

import type { EditorOptions } from '@tiptap/react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Editor, type EditorProps } from '@/components/editor';

import { useAutosave } from '@/hooks/use-autosave';
import { useLeaveOnArchived } from '@/hooks/use-leave-on-archived';
import { getCachedEditor } from '@/hooks/use-block-editor/editor-cache';
import { useSyncedEditorContent, withSyncedEditorContentWrite } from '@/hooks/use-synced-editor-content';
import {
  useEditorState,
  recordUserEdit,
  useAgentRunUiState,
  toPlainPendingReview,
  useExternalReviewResolution,
  type PendingReview,
  type PersistedDocument,
  type PersistedDocumentPatch,
  type PersistedDocumentWriter,
} from '@/hooks/llm/use-llm-agent';

import { editorAtom } from '@/state';

import { createBlankEditorContent } from '@/hooks/rxdb-models/use-notes';
import { guardOwnWrite, type MutationOptions } from '@/hooks/rxdb-base';

import { getEditorHTML, isEditorAlive } from '@/utils/editor-utils';

import type { RxDocument } from 'rxdb';
import type { Note, UserSource } from '@flintmint/core-shared/rxdb';

import workspaceStyles from './editor-workspace.module.scss';

type WorkspaceDocument = Note | UserSource;
type WorkspaceDocumentPatch = PersistedDocumentPatch & {
  name?: WorkspaceDocument['name'];
};

export const EditorWorkspace = <T extends WorkspaceDocument>(props: EditorWorkspaceProps<T>) => {
  const {
    id,
    title,
    header,
    draftId,
    document,
    editorType,
    onArchived,
    onTitleChange,
    upsertDocument,
    processing = false,
    domainSpecificEditor,
    editable = true,
    autosaveEnabled = true,
    isDocumentLoading: isLoading,
    ...rest
  } = props;

  const llmRequestId = id ?? draftId ?? editorType;
  const cachedEditor = getCachedEditor(llmRequestId);
  const cachedRequestEditor = cachedEditor?.editor;
  const cachedEditorSeedContent = isEditorAlive(cachedRequestEditor) ? getEditorHTML(cachedRequestEditor) : null;
  const blankEditorContent = useMemo(() => createBlankEditorContent(), []);
  const editorContent = document ? document.content : blankEditorContent;
  const initialEditorContent = cachedEditorSeedContent ?? editorContent;

  const editorDocumentId = id ?? draftId;
  const documentKey = document?.id ?? editorDocumentId ?? 'new';

  const [originalId] = useState(draftId || id);
  const [hasLoaded, setHasLoaded] = useState(!isLoading);
  if (!isLoading && !hasLoaded) {
    setHasLoaded(true);
  }
  const [ready, setReady] = useState(!!cachedEditor);
  const [, setEditorRevision] = useState(0);
  const [didInitNormalize, setDidInitNormalize] = useState(false);

  const { isLoading: isLLMRequestLoading } = useAgentRunUiState(llmRequestId);

  const [editor, setEditor] = useAtom(editorAtom);
  const editorState = useEditorState(llmRequestId);
  const titleSyncRef = useRef<{ documentKey?: string; name?: string }>({});
  // Mid-run the preview is painted imperatively into ProseMirror and autosave is
  // disabled, so re-rendering the editor container per transaction is pure waste.
  const suppressRevisionRef = useRef(false);
  suppressRevisionRef.current = isLLMRequestLoading || editorState !== 'interactive';

  const documentPendingReview = (document?.pendingReview as PendingReview | null | undefined) ?? null;
  const pendingReview = useMemo(
    () => (documentPendingReview ? toPlainPendingReview(documentPendingReview) : null),
    [documentPendingReview]
  );
  const hasPendingReview = (pendingReview?.suggestions.length ?? 0) > 0;

  useExternalReviewResolution({
    requestId: llmRequestId,
    documentKey,
    hasPendingReview,
    editor,
  });

  const onUpdate: EditorOptions['onUpdate'] = useCallback(() => {
    if (suppressRevisionRef.current) return;
    setEditorRevision((prev) => prev + 1);
  }, []);

  const getLiveEditor = () => {
    const liveEditor = isEditorAlive(editor) ? editor : null;
    const activeEditorDocumentId = liveEditor?.storage.documentInfo.documentId;

    if (liveEditor && activeEditorDocumentId === editorDocumentId) {
      return liveEditor;
    }

    if (isEditorAlive(cachedRequestEditor)) {
      return cachedRequestEditor;
    }

    return null;
  };

  const {
    hasUserEdits,
    getLiveContent: getLiveEditorContentForSave,
    markSynced,
  } = useSyncedEditorContent({
    documentKey,
    editorContent,
    getLiveEditor,
    documentContent: document?.content,
  });

  useEffect(() => {
    onTitleChange?.('');
    cachedEditor && setDidInitNormalize(true);
  }, []);

  useEffect(() => {
    if (document) {
      const documentName = document.name!;
      const previousTitleSync = titleSyncRef.current;
      const isNewDocument = previousTitleSync.documentKey !== documentKey;
      const isLocalTitleClean = title === previousTitleSync.name;

      titleSyncRef.current = { documentKey, name: documentName };

      if (isNewDocument || isLocalTitleClean) {
        onTitleChange?.(documentName);
      }

      setReady(true);
    }

    !id && setReady(true);
  }, [isLoading, document, hasPendingReview, documentKey, id, onTitleChange, title]);

  useEffect(() => {
    !!draftId && setDidInitNormalize(true);
  }, [draftId]);

  const canAutosaveDocument = !isLLMRequestLoading && editorState === 'interactive' && !hasPendingReview;
  const canAutosaveContent = canAutosaveDocument && !processing;
  // A saved route renders before its document syncs in. Nothing may be typed or saved until it
  // does: a write would land on the synced document unguarded and clobber it. Only a draft creates.
  const awaitingSavedDocument = !document && !draftId;
  const autosaveActive = (document ? !isLoading && ready : !!draftId) && didInitNormalize && autosaveEnabled;
  const autosaveName = title || document?.name || 'Untitled';
  const autosaveResetKey = useMemo(
    () => `${documentKey}:${document?.updatedAt ?? ''}:${document?.name ?? ''}`,
    [documentKey, document?.updatedAt, document?.name]
  );
  const persistDocumentPatch = useCallback<PersistedDocumentWriter>(
    async ({ content, pendingReview }) => {
      const payload: WorkspaceDocumentPatch = {
        ...(pendingReview !== undefined && { pendingReview }),
        ...(content !== undefined && { content }),
      };

      // Unguarded write, recorded so the next autosave can guard with its stamp — see guardOwnWrite.
      await guardOwnWrite(document, () => upsertDocument(payload));
    },
    [document, upsertDocument]
  );

  const { flush: flushAutosave } = useAutosave({
    // Note content stays out of `data`: diffing it here costs a full serialize and reparse on every
    // render. The editor reports whether it holds unsaved content instead.
    initialData: { name: document?.name || 'Untitled' },
    data: { name: autosaveName },
    hasContentChanges: canAutosaveContent && hasUserEdits,
    onSave: (autosaveData) => {
      const liveContent = getLiveEditorContentForSave();
      const latestAutosaveName = autosaveData.name ?? 'Untitled';
      const payload: WorkspaceDocumentPatch = {};

      // Compared against the stored revision, not the rendered one: a save issued while leaving runs
      // from the last render, which may predate a save that already wrote this name or content.
      const storedDocument = document?.getLatest();

      if (!storedDocument || latestAutosaveName !== (storedDocument.name || 'Untitled')) {
        payload.name = latestAutosaveName;
      }

      const willWriteContent = canAutosaveContent && hasUserEdits && (!storedDocument || liveContent !== storedDocument.content);
      if (willWriteContent) {
        payload.content = liveContent;
      }

      if (Object.keys(payload).length === 0) {
        return Promise.resolve();
      }

      const write = guardOwnWrite(document, (ifUnmodifiedSince) =>
        willWriteContent && document ? upsertDocument(payload, { ifUnmodifiedSince }) : upsertDocument(payload)
      );

      return withSyncedEditorContentWrite(write, {
        willWrite: willWriteContent,
        liveContent,
        markSynced,
      }).then((saved) => {
        if (saved === undefined) return saved;

        // User-vs-agent discriminator: this onSave only runs on interactive user commits, so it is
        // the note-edited signal. The store's isNoteDocument guard covers non-note editors.
        recordUserEdit(llmRequestId, saved as PersistedDocument);

        return saved;
      });
    },
    enabled: autosaveActive,
    resetKey: autosaveResetKey,
    flushOnLeave: true,
  });

  useLeaveOnArchived(document, async () => {
    if (autosaveActive) {
      try {
        await flushAutosave();
      } catch {
        // Reported by the base-layer mutation.
      }
    }

    onArchived?.();
  });

  return (
    <>
      {header}
      {domainSpecificEditor || (
        <>
          <Editor
            documentId={editorDocumentId}
            editorType={editorType}
            cacheable
            selfScrollable
            className={workspaceStyles.workspaceEditor}
            canSync={!hasUserEdits}
            onContentSynced={markSynced}
            didInitNormalize={didInitNormalize}
            setDidInitNormalize={setDidInitNormalize}
            content={initialEditorContent}
            pendingReview={pendingReview}
            persistDocumentPatch={persistDocumentPatch}
            llmRequestId={llmRequestId}
            onUpdate={onUpdate}
            setEditor={setEditor}
            processing={processing}
            editable={editable && !awaitingSavedDocument}
            key={`${originalId === id && isLoading && !hasLoaded ? 1 : 2}`}
            {...rest}
          />
        </>
      )}
    </>
  );
};

interface EditorWorkspaceProps<T> extends Partial<EditorProps> {
  id?: string;
  draftId?: string;
  title?: string;
  header?: ReactNode;
  editorType: string;
  autofocus?: boolean;
  processing?: boolean;
  autosaveEnabled?: boolean;
  onTitleChange?: (title: string) => void;
  /** Called once the document has been archived and any pending autosave has been flushed. */
  onArchived?: () => void;
  document?: RxDocument<T>; // may be undefined if isDraft
  isDocumentLoading: boolean;
  upsertDocument: (
    payload: WorkspaceDocumentPatch,
    options?: Pick<MutationOptions, 'ifUnmodifiedSince'>
  ) => Promise<RxDocument<T>>;
  domainSpecificEditor?: ReactNode;
}
